import React, { useEffect, useMemo } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { formatBangkokDateTime } from '../../utilities/bangkokDateTime';
import {
  getPhotoBranchDistanceMeters,
  getPhotoGoogleMapsUrl,
  getValidPhotoCoordinates,
  IPhotoCoordinates,
} from '../../utilities/photoLocation';

export interface DeliveryPhotoLocationPanelProps {
  location?: {
    latitude?: number | string | null;
    longitude?: number | string | null;
    source?: 'photo_gps' | 'unverified';
    capturedAt?: string | null;
    locationRecordedAt?: string | null;
    accuracyMeters?: number | null;
  };
  branch?: {
    latitude?: number | string | null;
    longitude?: number | string | null;
    name?: string;
    label?: string;
  };
}

const PHOTO_COLOR = '#ffbd59';
const BRANCH_COLOR = '#61d5f6';
const MUTED_COLOR = 'rgba(255, 255, 255, 0.68)';
const TILE_OPTIONS = { attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' };
const BRANCH_MARKER_OPTIONS = { radius: 11 };
const PHOTO_MARKER_OPTIONS = { radius: 6 };

const coordinateLabel = (coordinates: IPhotoCoordinates): string =>
  `${coordinates.latitude.toFixed(7)}, ${coordinates.longitude.toFixed(7)}`;

const MapViewport: React.FC<{ photo: IPhotoCoordinates; branch: IPhotoCoordinates | null }> = ({ photo, branch }) => {
  const map = useMap();

  useEffect(() => {
    const fitMap = () => {
      map.invalidateSize();
      const points: [number, number][] = [[photo.latitude, photo.longitude]];
      if (branch) points.push([branch.latitude, branch.longitude]);
      map.fitBounds(points, { padding: [24, 24], maxZoom: 17, animate: false });
    };
    const frame = window.requestAnimationFrame(fitMap);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fitMap);
    observer?.observe(map.getContainer());
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [map, photo, branch]);

  return null;
};

const DeliveryPhotoLocationPanel: React.FC<DeliveryPhotoLocationPanelProps> = ({ location, branch }) => {
  const photoCoordinates = useMemo(
    () => getValidPhotoCoordinates(location?.latitude, location?.longitude),
    [location?.latitude, location?.longitude],
  );
  const branchCoordinates = useMemo(
    () => getValidPhotoCoordinates(branch?.latitude, branch?.longitude),
    [branch?.latitude, branch?.longitude],
  );
  const verifiedCapture = location?.source === 'photo_gps';
  const photoLabel = verifiedCapture ? 'พิกัดขณะถ่ายภาพ (GPS อุปกรณ์)' : 'พิกัดที่แนบกับรูป (ไม่ยืนยันจุดถ่าย)';
  const branchLabel = branch?.label || 'พิกัดร้าน';
  const distance = photoCoordinates && branchCoordinates
    ? getPhotoBranchDistanceMeters(photoCoordinates, branchCoordinates)
    : null;
  const accuracy = location?.accuracyMeters;
  const mapCenter: [number, number] | undefined = photoCoordinates
    ? [photoCoordinates.latitude, photoCoordinates.longitude]
    : undefined;
  const mapOptions = {
    center: mapCenter,
    zoom: 17,
    scrollWheelZoom: false,
  };

  return (
    <Stack spacing={1.25} sx={{ color: 'common.white', py: 1 }}>
      <Typography variant="subtitle2" fontWeight={800}>พิกัดรูปภาพ</Typography>
      {location?.capturedAt && (
        <Box>
          <Typography variant="caption" sx={{ color: MUTED_COLOR }}>เวลาถ่ายภาพ (เวลาไทย)</Typography>
          <Typography variant="body2">
            {formatBangkokDateTime(location.capturedAt, 'DD/MM/BBBB HH:mm:ss')}
          </Typography>
        </Box>
      )}
      {location?.locationRecordedAt && (
        <Box>
          <Typography variant="caption" sx={{ color: MUTED_COLOR }}>เวลาพิกัด GPS (เวลาไทย)</Typography>
          <Typography variant="body2">
            {formatBangkokDateTime(location.locationRecordedAt, 'DD/MM/BBBB HH:mm:ss')}
          </Typography>
        </Box>
      )}
      {typeof accuracy === 'number' && Number.isFinite(accuracy) && accuracy >= 0 && (
        <Typography variant="caption" sx={{ color: MUTED_COLOR }}>
          ความแม่นยำ GPS ที่บันทึก: ±{accuracy.toLocaleString('th-TH', { maximumFractionDigits: 1 })} เมตร
        </Typography>
      )}
      {photoCoordinates ? (
        <>
          <Box>
            <Typography variant="caption" sx={{ color: PHOTO_COLOR }}>{photoLabel}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
              {coordinateLabel(photoCoordinates)}
            </Typography>
          </Box>
          <Box
            role="region"
            aria-label={`แผนที่พิกัดรูปภาพและ${branchLabel}`}
            sx={{ height: 220, borderRadius: 1, overflow: 'hidden', bgcolor: '#202a35' }}
          >
            <MapContainer
              {...mapOptions}
              style={{ width: '100%', height: '100%' }}
            >
              <TileLayer
                {...TILE_OPTIONS}
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {branchCoordinates && (
                <CircleMarker
                  center={[branchCoordinates.latitude, branchCoordinates.longitude]}
                  {...BRANCH_MARKER_OPTIONS}
                  pathOptions={{ color: BRANCH_COLOR, fill: false, weight: 3 }}
                >
                  <Tooltip>{branchLabel}{branch?.name ? `: ${branch.name}` : ''}</Tooltip>
                </CircleMarker>
              )}
              <CircleMarker
                center={[photoCoordinates.latitude, photoCoordinates.longitude]}
                {...PHOTO_MARKER_OPTIONS}
                pathOptions={{ color: '#73501d', fillColor: PHOTO_COLOR, fillOpacity: 1, weight: 2 }}
              >
                <Tooltip>{photoLabel}</Tooltip>
              </CircleMarker>
              <MapViewport photo={photoCoordinates} branch={branchCoordinates} />
            </MapContainer>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            <Typography variant="caption" sx={{ color: PHOTO_COLOR }}>● พิกัดรูป</Typography>
            {branchCoordinates && <Typography variant="caption" sx={{ color: BRANCH_COLOR }}>○ {branchLabel}</Typography>}
          </Stack>
          <Button
            component="a"
            href={getPhotoGoogleMapsUrl(photoCoordinates)}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ color: PHOTO_COLOR, borderColor: 'rgba(255, 189, 89, 0.45)', alignSelf: 'flex-start' }}
          >
            เปิดพิกัดรูปใน Google Maps
          </Button>
        </>
      ) : (
        <Typography variant="body2" sx={{ color: MUTED_COLOR }}>
          ไม่มีพิกัดจุดถ่ายที่บันทึกแยกสำหรับรูปนี้
        </Typography>
      )}
      <Box>
        <Typography variant="caption" sx={{ color: BRANCH_COLOR }}>
          {branchLabel}{branch?.name ? `: ${branch.name}` : ''}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: branchCoordinates ? 'monospace' : undefined, overflowWrap: 'anywhere' }}>
          {branchCoordinates ? coordinateLabel(branchCoordinates) : 'ไม่มีพิกัดอ้างอิงสำหรับเปรียบเทียบ'}
        </Typography>
      </Box>
      {distance !== null && (
        <Box>
          <Typography variant="body2">
            ระยะเส้นตรงจากพิกัดรูปถึงพิกัดอ้างอิง: {distance < 1000
              ? `${Math.round(distance).toLocaleString('th-TH')} เมตร`
              : `${(distance / 1000).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} กม.`}
          </Typography>
          <Typography variant="caption" sx={{ color: MUTED_COLOR }}>
            คำนวณจากพิกัดที่บันทึก ใช้ประกอบการตรวจสอบ
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export default DeliveryPhotoLocationPanel;
